import AnnotationAlert from "@uui/AnnotationAlert";
import { isEmpty } from "lodash/fp";

import Skeleton from "@mui/material/Skeleton";

import {
  CoachingModule,
  CoachingModuleContent,
  CoachingModuleEmptySummary,
  CoachingModuleHeader,
  CoachingModuleSummary,
  CoachingModuleTitle,
} from "@app/analytics/components/CoachingModule";
import ErrorState from "@app/analytics/components/ErrorState";
import Tip from "@app/analytics/components/Tip";
import BenchmarkOverlay, {
  BenchmarkDescription,
} from "@app/analytics/MeetingReport/components/BenchmarkOverlay";
import Moments from "@app/analytics/MeetingReport/components/Moments";
import { Moment } from "@app/analytics/MeetingReport/components/Moments/types";
import { LoadingSkeleton } from "@app/analytics/MeetingReport/styles";
import { TOOLTIPS } from "@app/common/content";
import { pluralizePhrase } from "@app/common/utils";
import { getInterruptionsSemanticScore } from "@app/common/utils/coachingUtils";

import Stack from "@rui/Stack";
import { Bold } from "@rui/styles";

import useCoachingData from "./hooks/useCoachingData";
import InsufficientData from "./InsufficientData";
import { Description } from "./styles";

const InterruptionsSummary = ({
  isEmpty,
  isLoading,
  numInterruptions,
}: {
  isEmpty: boolean;
  isLoading: boolean;
  numInterruptions: number;
}) => {
  if (isLoading) {
    return <Skeleton width={120} />;
  }

  if (isEmpty) {
    return (
      <CoachingModuleEmptySummary>
        Not enough talk time
      </CoachingModuleEmptySummary>
    );
  }

  const semanticScore = getInterruptionsSemanticScore(numInterruptions);

  return (
    <BenchmarkOverlay
      renderControl={({ onClick }) => (
        <CoachingModuleSummary onClick={onClick} icon={semanticScore}>
          {pluralizePhrase(numInterruptions, "instance")}
        </CoachingModuleSummary>
      )}
      title="Benchmark"
    >
      <BenchmarkDescription sx={{ whiteSpace: "wrap" }}>
        While there’s no benchmark for this metric, the best meetings have no
        interruptions and rely on an agenda or turn-taking.
      </BenchmarkDescription>
    </BenchmarkOverlay>
  );
};

const InterruptionsModule = ({
  onClickMoment,
}: {
  onClickMoment: (m: Moment) => (() => void) | undefined;
}) => {
  const {
    insufficientTalkTimeData,
    interruptions,
    isError,
    isInitialLoading,
    refetch,
  } = useCoachingData();

  const moments = interruptions.moments;

  // even if talk time was "too low", still show moments if any were detected
  const showEmptyState = insufficientTalkTimeData && isEmpty(moments);

  return (
    <CoachingModule expandable>
      <CoachingModuleHeader>
        <CoachingModuleTitle
          Icon={AnnotationAlert}
          tooltip={TOOLTIPS.coaching.interruptions}
        >
          Interruptions
        </CoachingModuleTitle>
        <InterruptionsSummary
          isEmpty={showEmptyState}
          isLoading={isInitialLoading}
          numInterruptions={interruptions.totalCount}
        />
      </CoachingModuleHeader>
      <CoachingModuleContent>
        {isError ? (
          <ErrorState refetch={refetch} sx={{ minHeight: "150px" }} />
        ) : showEmptyState ? (
          <InsufficientData type="talk_time">
            Your talk time was too low to detect interruptions.
          </InsufficientData>
        ) : (
          <Stack spacing={1.5}>
            {isInitialLoading ? (
              <LoadingSkeleton height={50} />
            ) : isEmpty(moments) ? (
              <Description>
                Read didn't detect that you interrupted anyone during this
                meeting. Great job!
              </Description>
            ) : (
              <Stack spacing={1}>
                <Description>
                  Review{" "}
                  <Bold>{pluralizePhrase(moments.length, "moment")}</Bold> where
                  you interrupted others:
                </Description>
                <Moments moments={moments} onClickMoment={onClickMoment} />
              </Stack>
            )}
            <Tip>
              Sometimes, interruptions are unavoidable. Pausing between thoughts
              gives others a chance to chime in, and an agenda or meeting timer
              can ensure everyone gets a turn to speak.
            </Tip>
          </Stack>
        )}
      </CoachingModuleContent>
    </CoachingModule>
  );
};

export default InterruptionsModule;
